import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import LinearProgress from "@mui/material/LinearProgress";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { useTheme } from "@mui/material/styles";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTranslation } from "react-i18next";
import {
  DASHBOARD_GRAPH_PERIODS,
  DEFAULT_DASHBOARD_GRAPH_PERIOD,
  useHourlyActivityAverageGraphQuery,
} from "@query/dashboardGraphQuery";
import { localizeHourlyBuckets, resolveDashboardLocale } from "../utils/chartLocale";

const PERIOD_TRANSLATION_KEYS = {
  "1year": "main:dashboard.periods.oneYear",
  "6months": "main:dashboard.periods.sixMonths",
  "3months": "main:dashboard.periods.threeMonths",
  "1month": "main:dashboard.periods.oneMonth",
  "1week": "main:dashboard.periods.oneWeek",
};

export default function ActivityHourlyAverageChart() {
  const theme = useTheme();
  const { t, i18n } = useTranslation(["main"]);
  const [period, setPeriod] = React.useState(DEFAULT_DASHBOARD_GRAPH_PERIOD);
  const chartHeight = 320;

  const hourlyGraphQuery = useHourlyActivityAverageGraphQuery(period);
  const payload = hourlyGraphQuery.data?.data;
  const locale = resolveDashboardLocale(i18n.language);

  const numberFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1,
      }),
    [locale]
  );

  const hourlyData = React.useMemo(() => {
    const hourlyList = Array.isArray(payload?.hourly) ? payload.hourly : [];
    const hourlyMap = new Map();

    hourlyList.forEach((point) => {
      if (Number.isInteger(point.hour) && point.hour >= 0 && point.hour <= 23) {
        // Keep backward compatibility while supporting the updated API payload.
        const averageUsersCount = Number(point.average_users_count ?? point.average_minutes) || 0;
        hourlyMap.set(point.hour, averageUsersCount);
      }
    });

    return localizeHourlyBuckets(
      Array.from({ length: 24 }, (_, hour) => ({
        hour,
        averageUsersCount: hourlyMap.get(hour) || 0,
      })),
      locale,
      payload?.to_date
    );
  }, [locale, payload?.hourly, payload?.to_date]);

  const hasData = Array.isArray(payload?.hourly) && payload.hourly.length > 0;

  const peakHourPoint = React.useMemo(() => {
    if (hourlyData.length === 0) {
      return null;
    }

    return hourlyData.reduce(
      (peak, point) =>
        point.averageUsersCount > (peak?.averageUsersCount || -1) ? point : peak,
      null
    );
  }, [hourlyData]);

  const peakLabel =
    peakHourPoint && peakHourPoint.averageUsersCount > 0
      ? `${peakHourPoint.label} (${numberFormatter.format(peakHourPoint.averageUsersCount)} ${t(
          "main:dashboard.activity.usersShort"
        )})`
      : t("main:dashboard.activity.noPeak");

  const isLoading = hourlyGraphQuery.isLoading && !payload;

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        borderRadius: 3,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(180deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0) 35%)",
      }}
    >
      {hourlyGraphQuery.isFetching && !isLoading ? <LinearProgress /> : <Box sx={{ height: 4 }} />}
      <CardContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", flex: 1 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            alignItems: { xs: "stretch", sm: "flex-start" },
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
              <AccessTimeRoundedIcon color="success" fontSize="small" />
              <Typography component="h2" variant="subtitle2">
                {t("main:dashboard.activity.title")}
              </Typography>
            </Stack>
            <Typography variant="h6" component="p" sx={{ fontWeight: 700 }}>
              {peakLabel}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("main:dashboard.activity.subtitle")}
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 170 } }}>
            <InputLabel id="activity-period-label">{t("main:dashboard.period.label")}</InputLabel>
            <Select
              labelId="activity-period-label"
              id="activity-period"
              value={period}
              label={t("main:dashboard.period.label")}
              onChange={(event) => setPeriod(event.target.value)}
            >
              {DASHBOARD_GRAPH_PERIODS.map((periodValue) => (
                <MenuItem key={periodValue} value={periodValue}>
                  {t(PERIOD_TRANSLATION_KEYS[periodValue])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Box sx={{ mt: "auto" }}>
          {isLoading ? <Skeleton variant="rounded" height={chartHeight} /> : null}

          {!isLoading && hourlyGraphQuery.isError ? (
            <Alert severity="error">{t("main:dashboard.errors.loadActivity")}</Alert>
          ) : null}

          {!isLoading && !hourlyGraphQuery.isError && !hasData ? (
            <Box
              sx={{
                height: chartHeight,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("main:dashboard.noData")}
              </Typography>
            </Box>
          ) : null}

          {!isLoading && !hourlyGraphQuery.isError && hasData ? (
            <BarChart
              borderRadius={5}
              colors={[theme.palette.success.main]}
              xAxis={[
                {
                  scaleType: "band",
                  categoryGapRatio: 0.45,
                  data: hourlyData.map((point) => point.label),
                  tickInterval: (_, index) => index % 2 === 0,
                },
              ]}
              series={[
                {
                  id: "hourly-average",
                  label: t("main:dashboard.activity.users"),
                  data: hourlyData.map((point) => point.averageUsersCount),
                  valueFormatter: (value) =>
                    `${numberFormatter.format(value ?? 0)} ${t(
                      "main:dashboard.activity.usersShort"
                    )}`,
                },
              ]}
              height={chartHeight}
              margin={{ left: 60, right: 20, top: 24, bottom: 36 }}
              grid={{ horizontal: true }}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}
            />
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
